import React, { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import Content from '../../components/Content';
import Table from '../../components/Table';
import TextInput from '../../components/TextInput';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';
import Pagination from '../../components/Pagination';
import usePagination from '../../components/Pagination/usePagination';
import {
  checkAttackHealth,
  getRecentAlerts,
  enrichAlert,
  mapTechnique,
  getCoOccurrences,
  getTechniques,
  getDeciderUrl
} from '../../redux/actions/attackActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';
import styles from './ATTACKMapping.module.css';

const FRONTEND_ATTACK_MAPPING_ERROR_CODES = {
  INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  HEALTH_CHECK_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  TECHNIQUES_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  ALERTS_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  ENRICH_ALERT_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  MAP_TECHNIQUE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  MAP_TECHNIQUE_VALIDATION_FAILED: ErrorCodes.API_INVALID_PARAMETER,
  BATCH_ANALYSIS_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  BATCH_ANALYSIS_VALIDATION_FAILED: ErrorCodes.API_INVALID_PARAMETER,
  DECIDER_URL_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildAttackMappingSupportError = (errorCode, pageInfo, sourceMessage) =>
  formatFrontendSupportError({
    errorCode,
    pageInfo,
    sourceMessage,
  });

const getErrorMessage = (source, fallbackMessage) => {
  if (typeof source === 'string' && source.trim()) {
    return source.trim();
  }

  if (source && typeof source === 'object') {
    if (typeof source.msg === 'string' && source.msg.trim()) {
      return source.msg.trim();
    }
    if (typeof source.message === 'string' && source.message.trim()) {
      return source.message.trim();
    }
  }

  return fallbackMessage;
};

const ATTACKMappingScreen = () => {
  const dispatch = useDispatch();

  // State
  const [activeTab, setActiveTab] = useState('recent-alerts');
  const [loading, setLoading] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [techniqueInput, setTechniqueInput] = useState('');
  const [techniqueNameInput, setTechniqueNameInput] = useState('');
  const [notesInput, setNotesInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [batchTechniqueIds, setBatchTechniqueIds] = useState('');
  const [relatedTechniques, setRelatedTechniques] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [supportErrorMessage, setSupportErrorMessage] = useState('');
  const [copiedSupportError, setCopiedSupportError] = useState(false);

  // Redux state
  const attackHealth = useSelector((state) => state.attackHealth);
  const { connected, url: deciderUrl, version } = attackHealth;
  
  const attackRecentAlerts = useSelector((state) => state.attackRecentAlerts);
  const { alerts = [] } = attackRecentAlerts;

  const attackTechniques = useSelector((state) => state.attackTechniques);
  const { patterns = [] } = attackTechniques;

  // Stats
  const totalMappedAlerts = alerts.filter(alert => alert.mappings && alert.mappings.length > 0).length;
  const uniqueTechniques = new Set(
    alerts.flatMap(alert => 
      (alert.mappings || []).map(m => m.technique_id)
    )
  ).size;
  const uniqueTactics = new Set(
    alerts.flatMap(alert => 
      (alert.mappings || []).map(m => m.tactic_id).filter(Boolean)
    )
  ).size;

  // Client-side pagination for the Recent Alerts table
  const { pageRows, pageNumber, totalRows, pageLimit, handlePageChange } = usePagination(alerts);

  const setAttackMappingSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
    setSupportErrorMessage(buildAttackMappingSupportError(errorCode, pageInfo, sourceMessage));
    setCopiedSupportError(false);
  }, []);

  const clearAttackMappingSupportError = React.useCallback((force = false) => {
    if (!force) {
      return;
    }
    setSupportErrorMessage('');
    setCopiedSupportError(false);
  }, []);

  const handleCopySupportError = React.useCallback(async () => {
    if (!supportErrorMessage) return;
    try {
      await navigator.clipboard.writeText(supportErrorMessage);
      setCopiedSupportError(true);
      setTimeout(() => setCopiedSupportError(false), 2000);
    } catch (error) {
      setCopiedSupportError(false);
    }
  }, [supportErrorMessage]);

  const notifyAttackMappingError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
    const formattedError = buildAttackMappingSupportError(errorCode, pageInfo, sourceMessage);
    setSupportErrorMessage(formattedError);
    setCopiedSupportError(false);
    toast.error(formattedError);
  }, []);

  // Check Decider health and load techniques on mount
  useEffect(() => {
    const initializeAttackMapping = async () => {
      const [healthResult, techniquesResult] = await Promise.all([
        dispatch(checkAttackHealth()),
        dispatch(getTechniques())
      ]);

      if (!healthResult || !healthResult.status) {
        setAttackMappingSupportError(
          FRONTEND_ATTACK_MAPPING_ERROR_CODES.HEALTH_CHECK_FAILED,
          'ATT&CK Mapping Page / Decider Health Check Failed',
          getErrorMessage(healthResult, 'Failed to check Decider health')
        );
      }

      if (!techniquesResult || !techniquesResult.status) {
        setAttackMappingSupportError(
          FRONTEND_ATTACK_MAPPING_ERROR_CODES.TECHNIQUES_LOAD_FAILED,
          'ATT&CK Mapping Page / Techniques Load Failed',
          getErrorMessage(techniquesResult, 'Failed to load ATT&CK techniques')
        );
      }
    };

    initializeAttackMapping();
  }, [dispatch, setAttackMappingSupportError]);

  const handleLoadAlerts = React.useCallback(async (showToast = false) => {
    setLoading(true);
    const result = await dispatch(getRecentAlerts({ limit: 100 }));

    if (result && result.status) {
      setHasError(false);
      if (showToast) {
        toast.success('Recent alerts refreshed');
      }
    } else {
      setHasError(true);
      const sourceMessage = getErrorMessage(result, 'Failed to fetch recent alerts');
      setAttackMappingSupportError(
        FRONTEND_ATTACK_MAPPING_ERROR_CODES.ALERTS_LOAD_FAILED,
        'ATT&CK Mapping Page / Recent Alerts Load Failed',
        sourceMessage
      );
      if (showToast) {
        toast.error(sourceMessage);
      }
    }

    setLoading(false);
    return result;
  }, [dispatch, setAttackMappingSupportError]);

  // Load alerts when tab changes to recent-alerts
  useEffect(() => {
    if (activeTab === 'recent-alerts') {
      handleLoadAlerts();
    }
  }, [activeTab, handleLoadAlerts]);

  const handleEnrichAlert = async (alert) => {
    setSelectedAlert(alert);
    setLoading(true);
    
    const result = await dispatch(enrichAlert(alert.id, alert.alert));
    
    if (result.status && result.suggestions) {
      setSuggestions(result.suggestions);
      setShowMappingModal(true);
    } else if (!result.status) {
      setAttackMappingSupportError(
        FRONTEND_ATTACK_MAPPING_ERROR_CODES.ENRICH_ALERT_FAILED,
        'ATT&CK Mapping Page / Alert Enrichment Failed',
        getErrorMessage(result, 'Failed to enrich alert with ATT&CK techniques')
      );
    }
    
    setLoading(false);
  };

  const handleMapTechnique = async (confidence = 'manual') => {
    if (!techniqueInput || !selectedAlert) {
      notifyAttackMappingError(
        FRONTEND_ATTACK_MAPPING_ERROR_CODES.MAP_TECHNIQUE_VALIDATION_FAILED,
        'ATT&CK Mapping Page / Technique Mapping Validation Failed',
        'Technique ID is required'
      );
      return;
    }

    setLoading(true);
    const result = await dispatch(mapTechnique(
      selectedAlert.id,
      techniqueInput,
      techniqueNameInput,
      confidence,
      notesInput
    ));

    if (result.status) {
      setShowMappingModal(false);
      setTechniqueInput('');
      setTechniqueNameInput('');
      setNotesInput('');
      setSuggestions([]);
      await handleLoadAlerts(); // Reload alerts
    } else {
      setAttackMappingSupportError(
        FRONTEND_ATTACK_MAPPING_ERROR_CODES.MAP_TECHNIQUE_FAILED,
        'ATT&CK Mapping Page / Technique Mapping Failed',
        getErrorMessage(result, 'Failed to map ATT&CK technique')
      );
    }
    
    setLoading(false);
  };

  const handleApplySuggestion = (suggestion) => {
    setTechniqueInput(suggestion.technique_id);
    setTechniqueNameInput(suggestion.technique_name);
    handleMapTechnique('predefined');
  };

  const handleOpenInDecider = async (techId = null, tacticId = null) => {
    const result = await dispatch(getDeciderUrl(techId, tacticId));
    if (!result || !result.status) {
      setAttackMappingSupportError(
        FRONTEND_ATTACK_MAPPING_ERROR_CODES.DECIDER_URL_FAILED,
        'ATT&CK Mapping Page / Decider URL Generation Failed',
        getErrorMessage(result, 'Failed to generate Decider URL')
      );
    }
  };

  const handleBatchAnalysis = async () => {
    const ids = batchTechniqueIds.split(',').map(id => id.trim()).filter(Boolean);
    
    if (ids.length === 0) {
      notifyAttackMappingError(
        FRONTEND_ATTACK_MAPPING_ERROR_CODES.BATCH_ANALYSIS_VALIDATION_FAILED,
        'ATT&CK Mapping Page / Batch Analysis Validation Failed',
        'Please enter technique IDs separated by commas'
      );
      return;
    }

    setLoading(true);
    const result = await dispatch(getCoOccurrences(ids));
    
    if (result.status && result.related_techniques) {
      setRelatedTechniques(result.related_techniques);
    } else {
      setAttackMappingSupportError(
        FRONTEND_ATTACK_MAPPING_ERROR_CODES.BATCH_ANALYSIS_FAILED,
        'ATT&CK Mapping Page / Batch Analysis Failed',
        getErrorMessage(result, 'Failed to analyze technique co-occurrences')
      );
    }
    
    setLoading(false);
  };

  const renderConnectionStatus = () => (
    <div className={`${styles.statusBanner} ${connected ? styles.connected : styles.disconnected}`}>
      <div className={styles.statusContent}>
        <div className={styles.statusIcon}>
          {connected ? '✓' : '✗'}
        </div>
        <div className={styles.statusText}>
          <strong>Decider Status:</strong>{' '}
          {connected ? (
            <>
              Connected to {deciderUrl} (Version: {version})
            </>
          ) : (
            'Not connected - Check if Decider is running'
          )}
        </div>
        {connected && (
          <button 
            className={styles.openDeciderBtn}
            onClick={() => handleOpenInDecider()}
          >
            Open Decider
          </button>
        )}
      </div>
    </div>
  );

  const renderStats = () => (
    <div className={styles.statsContainer}>
      <div className={styles.statCard}>
        <div className={styles.statValue}>{totalMappedAlerts}</div>
        <div className={styles.statLabel}>Mapped Alerts</div>
      </div>
      <div className={styles.statCard}>
        <div className={styles.statValue}>{uniqueTechniques}</div>
        <div className={styles.statLabel}>Techniques Detected</div>
      </div>
      <div className={styles.statCard}>
        <div className={styles.statValue}>{uniqueTactics}</div>
        <div className={styles.statLabel}>Tactics Involved</div>
      </div>
      <div className={styles.statCard}>
        <div className={styles.statValue}>{patterns.filter(p => p.enabled).length}</div>
        <div className={styles.statLabel}>Active Patterns</div>
      </div>
    </div>
  );

  const renderTabs = () => (
    <div className={styles.tabContainer}>
      <button
        className={`${styles.tab} ${activeTab === 'recent-alerts' ? styles.activeTab : ''}`}
        onClick={() => setActiveTab('recent-alerts')}
      >
        Recent Alerts
      </button>
      <button
        className={`${styles.tab} ${activeTab === 'technique-explorer' ? styles.activeTab : ''}`}
        onClick={() => setActiveTab('technique-explorer')}
      >
        Technique Explorer
      </button>
      <button
        className={`${styles.tab} ${activeTab === 'batch-analysis' ? styles.activeTab : ''}`}
        onClick={() => setActiveTab('batch-analysis')}
      >
        Batch Analysis
      </button>
    </div>
  );

  const renderRecentAlertsTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.tableHeader}>
        <h3>Recent Alerts with ATT&CK Mappings</h3>
        <button className={styles.refreshBtn} onClick={() => handleLoadAlerts(true)}>
          Refresh
        </button>
      </div>

      {hasError && alerts.length === 0 ? (
        <div>
          <NetworkError />
          {supportErrorMessage && (
            <div className={styles.supportErrorCard}>
              <strong>Support Error:</strong>
              <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                <button
                  type='button'
                  className={styles.copySupportErrorButton}
                  onClick={handleCopySupportError}
                >
                  {copiedSupportError ? 'Copied' : 'Copy Error Details'}
                </button>
                <button
                  type='button'
                  className={styles.copySupportErrorButton}
                  onClick={() => clearAttackMappingSupportError(true)}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
        <Table
          containerStyle={{ height: '60vh' }}
          tableHead={
            <tr>
              <th>Timestamp</th>
              <th>Watcher</th>
              <th>Alert</th>
              <th>Techniques</th>
              <th>Actions</th>
            </tr>
          }
          tableRow={
            pageRows.length > 0 ? (
              pageRows.map((alert, index) => (
                <tr key={index}>
                  <td>{alert.timestamp}</td>
                  <td>{alert.watcher_name}</td>
                  <td className={styles.alertCell}>{alert.alert}</td>
                  <td>
                    <div className={styles.techniqueBadges}>
                      {alert.mappings && alert.mappings.length > 0 ? (
                        alert.mappings.map((mapping, idx) => (
                          <span
                            key={idx}
                            className={`${styles.techniqueBadge} ${styles[mapping.confidence]}`}
                            onClick={() => handleOpenInDecider(mapping.technique_id)}
                            title={`${mapping.technique_name}\nConfidence: ${mapping.confidence}\nMapped by: ${mapping.mapped_by}`}
                          >
                            {mapping.technique_id}
                          </span>
                        ))
                      ) : (
                        <span className={styles.noMapping}>No mapping</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <button
                      className={styles.enrichBtn}
                      onClick={() => handleEnrichAlert(alert)}
                    >
                      {alert.mappings && alert.mappings.length > 0 ? 'Edit' : 'Map'}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center' }}>
                  No alerts found
                </td>
              </tr>
            )
          }
        />
        <div className='paginationContainer'>
          <Pagination
            onPageChange={handlePageChange}
            rowsPerPage={pageLimit}
            totalPageSize={totalRows}
            pageNumber={pageNumber}
          />
        </div>
        </>
      )}
    </div>
  );

  const renderTechniqueExplorerTab = () => (
    <div className={styles.tabContent}>
      <h3>Predefined ICS/OT Attack Patterns</h3>
      <p className={styles.description}>
        These patterns are used to automatically enrich alerts with ATT&CK techniques.
      </p>
      
      <Table
        containerStyle={{ height: '60vh' }}
        tableHead={
          <tr>
            <th>Technique ID</th>
            <th>Technique Name</th>
            <th>Alert Pattern</th>
            <th>Description</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        }
        tableRow={
          patterns.length > 0 ? (
            patterns.map((pattern, index) => (
              <tr key={index}>
                <td>
                  <span 
                    className={styles.techniqueLink}
                    onClick={() => handleOpenInDecider(pattern.technique_id)}
                  >
                    {pattern.technique_id}
                  </span>
                </td>
                <td>{pattern.technique_name}</td>
                <td><code>{pattern.alert_pattern}</code></td>
                <td>{pattern.description}</td>
                <td>
                  <span className={`${styles.statusBadge} ${pattern.enabled ? styles.enabled : styles.disabled}`}>
                    {pattern.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td>
                  <button
                    className={styles.viewBtn}
                    onClick={() => handleOpenInDecider(pattern.technique_id)}
                  >
                    View in Decider
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" style={{ textAlign: 'center' }}>
                No patterns found
              </td>
            </tr>
          )
        }
      />
    </div>
  );

  const renderBatchAnalysisTab = () => (
    <div className={styles.tabContent}>
      <h3>Batch Technique Analysis</h3>
      <p className={styles.description}>
        Analyze multiple techniques together to discover commonly co-occurring techniques.
      </p>
      
      <div className={styles.batchInputContainer}>
        <TextInput
          label="Technique IDs (comma-separated)"
          placeholder="e.g., T0836, T0855, T0846"
          value={batchTechniqueIds}
          onChange={(e) => setBatchTechniqueIds(e.target.value)}
        />
        <button 
          className={styles.analyzeBtn}
          onClick={handleBatchAnalysis}
          disabled={!connected}
        >
          Analyze
        </button>
      </div>

      {relatedTechniques.length > 0 && (
        <div className={styles.resultsContainer}>
          <h4>Related Techniques Found: {relatedTechniques.length}</h4>
          <Table
            containerStyle={{ height: '50vh' }}
            tableHead={
              <tr>
                <th>Technique ID</th>
                <th>Technique Name</th>
                <th>Score</th>
                <th>Actions</th>
              </tr>
            }
            tableRow={
              relatedTechniques.slice(0, 20).map((tech, index) => (
                <tr key={index}>
                  <td>
                    <span 
                      className={styles.techniqueLink}
                      onClick={() => handleOpenInDecider(tech.tech_id)}
                    >
                      {tech.tech_id}
                    </span>
                  </td>
                  <td>{tech.tech_name}</td>
                  <td>
                    <div className={styles.scoreBar}>
                      <div 
                        className={styles.scoreBarFill}
                        style={{ width: `${Math.min(100, tech.score * 100)}%` }}
                      />
                      <span className={styles.scoreText}>{(tech.score * 100).toFixed(1)}%</span>
                    </div>
                  </td>
                  <td>
                    <button
                      className={styles.viewBtn}
                      onClick={() => handleOpenInDecider(tech.tech_id)}
                    >
                      View in Decider
                    </button>
                  </td>
                </tr>
              ))
            }
          />
        </div>
      )}
    </div>
  );

  const renderMappingModal = () => (
    showMappingModal && (
      <div className={styles.modalOverlay} onClick={() => setShowMappingModal(false)}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <h3>Map ATT&CK Technique</h3>
          
          {selectedAlert && (
            <div className={styles.alertInfo}>
              <p><strong>Alert:</strong> {selectedAlert.alert}</p>
              <p><strong>Timestamp:</strong> {selectedAlert.timestamp}</p>
            </div>
          )}

          {suggestions.length > 0 && (
            <div className={styles.suggestionsSection}>
              <h4>Suggested Techniques</h4>
              {suggestions.map((suggestion, index) => (
                <div key={index} className={styles.suggestionCard}>
                  <div className={styles.suggestionInfo}>
                    <strong>{suggestion.technique_id}</strong> - {suggestion.technique_name}
                    <p className={styles.suggestionDesc}>{suggestion.description}</p>
                  </div>
                  <button
                    className={styles.applyBtn}
                    onClick={() => handleApplySuggestion(suggestion)}
                  >
                    Apply
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className={styles.manualMapping}>
            <h4>Manual Mapping</h4>
            <TextInput
              label="Technique ID"
              placeholder="e.g., T0836"
              value={techniqueInput}
              onChange={(e) => setTechniqueInput(e.target.value.toUpperCase())}
            />
            <TextInput
              label="Technique Name (optional)"
              placeholder="e.g., Modify Parameter"
              value={techniqueNameInput}
              onChange={(e) => setTechniqueNameInput(e.target.value)}
            />
            <div className={styles.textareaGroup}>
              <label>Notes (optional)</label>
              <textarea
                className={styles.notesTextarea}
                placeholder="Add any notes about this mapping..."
                value={notesInput}
                onChange={(e) => setNotesInput(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button
              className={styles.cancelBtn}
              onClick={() => setShowMappingModal(false)}
            >
              Cancel
            </button>
            <button
              className={styles.mapBtn}
              onClick={() => handleMapTechnique('manual')}
              disabled={!techniqueInput}
            >
              Map Technique
            </button>
          </div>
        </div>
      </div>
    )
  );

  return (
    <Content currentMenu='attack' headerTitle='ATT&CK Mapping'>
      {loading && <Loader />}
      
      <Fragment>
        {supportErrorMessage && !(hasError && activeTab === 'recent-alerts' && alerts.length === 0) && (
          <div className={styles.supportErrorBanner}>
            <div className={styles.supportErrorBannerText}>
              <strong>Support Error:</strong>
              <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button
                type='button'
                className={styles.copySupportErrorButton}
                onClick={handleCopySupportError}
              >
                {copiedSupportError ? 'Copied' : 'Copy Error Details'}
              </button>
              <button
                type='button'
                className={styles.copySupportErrorButton}
                onClick={() => clearAttackMappingSupportError(true)}
              >
                Clear
              </button>
            </div>
          </div>
        )}
        {renderConnectionStatus()}
        {renderStats()}
        {renderTabs()}
        
        {activeTab === 'recent-alerts' && renderRecentAlertsTab()}
        {activeTab === 'technique-explorer' && renderTechniqueExplorerTab()}
        {activeTab === 'batch-analysis' && renderBatchAnalysisTab()}
        
        {renderMappingModal()}
      </Fragment>
    </Content>
  );
};

export default ATTACKMappingScreen;
